import { useId, type ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Shared form-control chrome (AUDIT_FIXES_JULY27 Items G/J1).
 *
 * One source of truth for the input class string that had drifted per-file
 * (`px-3 py-2` vs `px-2.5 py-1.5`), and the shared label/help/error contract
 * every primitive (`Input`, `Select`, `Textarea`, `FileInput`) follows:
 * an auto-generated stable `id` via `useId()` wired to a real `<label
 * htmlFor>`, overridable with an explicit `id`. `name`/`autoComplete` pass
 * through untouched — they only matter where a browser autofills (auth,
 * profile), and callers opt in there.
 */

export type FieldSize = 'default' | 'sm'

/** The bordered control look, adoptable on raw elements before (or without)
 * adopting the component — `default` is the dominant form style, `sm` the
 * dense filter-bar variant. Width is the caller's business.
 * The `--ring` focus chrome is part of the look (audit UI-4): keyboard focus
 * renders with the design system's ring, not the UA default. */
export function inputClass(size: FieldSize = 'default'): string {
  const focus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  return size === 'sm'
    ? `rounded-md border bg-background px-2.5 py-1.5 text-sm ${focus}`
    : `rounded-md border bg-background px-3 py-2 text-sm ${focus}`
}

export interface FieldChromeProps {
  /** Visible caption (or accessible name when `hideLabel`). Required — an
   * unnamed control is exactly the audit finding these primitives close. */
  label: string
  /** Keep the accessible name but render no visible caption (dense rows). */
  hideLabel?: boolean | undefined
  /** Muted single-line hint under the control. */
  help?: string | undefined
  /** Field-scoped error; rendered `role="alert"` under the control. */
  error?: string | undefined
  size?: FieldSize | undefined
  /** Class for the outer wrapper (widths live here, not on the control). */
  wrapperClassName?: string | undefined
}

/** Stable per-instance control id: the explicit one when given, else useId. */
/**
 * F35: the help line (a setting's one-sentence description) and the error are
 * READ by screen readers with the control, not only shown under it. The ids
 * are derived from the field id so every primitive wires them the same way.
 */
export function fieldHelpId(id: string): string {
  return `${id}-help`
}
export function fieldErrorId(id: string): string {
  return `${id}-error`
}
/** `aria-describedby` for a control whose shell shows `help` / `error`. */
export function fieldDescribedBy(id: string, help: string | undefined, error: string | undefined): string | undefined {
  if (error) return fieldErrorId(id)
  return help ? fieldHelpId(id) : undefined
}

export function useFieldId(explicit: string | undefined): string {
  const auto = useId()
  return explicit ?? auto
}

export function FieldShell({
  id,
  label,
  hideLabel,
  help,
  error,
  required,
  wrapperClassName,
  children,
}: {
  id: string
  required?: boolean | undefined
  children: ReactNode
} & Omit<FieldChromeProps, 'size'>) {
  return (
    <div className={cn('text-sm', wrapperClassName)}>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'mb-1 block text-muted-foreground'}>
        {label}
        {required && !hideLabel ? ' *' : ''}
      </label>
      {children}
      {help && !error && (
        <p id={fieldHelpId(id)} className="mt-1 text-xs text-muted-foreground">
          {help}
        </p>
      )}
      {error && (
        <p id={fieldErrorId(id)} role="alert" className="mt-1 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
